import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, Lock, Mail, ArrowLeft, Loader2, Eye, EyeOff, BarChart3, Users, Zap, Shield, CheckCircle } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_539426ce-fb0d-461c-961c-2751960afc8c/artifacts/fhqddo5o_Dnexus%20nexus%20logo%20Dark.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_539426ce-fb0d-461c-961c-2751960afc8c/artifacts/r2mo7zys_Dnexus%20Logo%20Light.png";

const font = { fontFamily: "Inter, sans-serif" };
const fontHead = { fontFamily: "Poppins, sans-serif" };

const Login = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { theme, setTheme } = useTheme();
  const [checking, setChecking] = useState(true);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [slugBusiness, setSlugBusiness] = useState(null);

  useEffect(() => { setTheme("dark"); }, [setTheme]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: "include", mode: "cors", headers: { "Accept": "application/json" }
        });
        if (response.ok) {
          const user = await response.json();
          localStorage.setItem("user_info", JSON.stringify({
            user_id: user.user_id, name: user.name, role: user.role, email: user.email, logged_in: true
          }));
          window.location.replace("/dashboard");
        } else {
          localStorage.removeItem("user_info");
          setChecking(false);
        }
      } catch (error) {
        localStorage.removeItem("user_info");
        setChecking(false);
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (slug) {
      fetch(`${BACKEND_URL}/api/public/business/${slug}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => setSlugBusiness(data))
        .catch(() => setSlugBusiness(null));
    }
  }, [slug]);

  const handleGoogleLogin = () => {
    window.location.href = `${BACKEND_URL}/api/auth/google`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error("Please enter email and password");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        credentials: "include", mode: "cors",
        body: JSON.stringify(loginData),
      });
      if (response.ok) {
        const user = await response.json();
        localStorage.setItem("user_info", JSON.stringify({
          user_id: user.user_id, name: user.name, role: user.role, email: user.email, logged_in: true
        }));
        toast.success(`Welcome back, ${user.name}!`);
        window.location.replace("/dashboard");
      } else {
        const error = await response.json();
        toast.error(error.detail || (response.status === 403 ? "Account access restricted" : "Invalid email or password"));
      }
    } catch (error) {
      toast.error("Login failed. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
          <p className="text-sm text-gray-500" style={font}>Loading...</p>
        </div>
      </div>
    );
  }

  const features = [
    { icon: BarChart3, label: "Sales & CRM", desc: "Track leads, deals, and revenue in real time" },
    { icon: Users, label: "HR & Payroll", desc: "Manage your team, attendance, and payroll" },
    { icon: Zap, label: "Automation", desc: "Automate workflows and save hours daily" },
    { icon: Shield, label: "Enterprise Security", desc: "Bank-grade encryption & role-based access" },
  ];

  return (
    <div className="min-h-screen flex bg-gray-950">
      {/* Left: Brand panel with gradient */}
      <div className="hidden lg:flex lg:w-[50%] relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-400/5 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] bg-indigo-500/5 rounded-full blur-[80px]" />

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
          backgroundSize: "60px 60px"
        }} />

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* Logo */}
          <div>
            <Link to="/">
              <img src={LOGO_LIGHT} alt="Dnexus" className="h-12 w-auto" />
            </Link>
          </div>

          {/* Main content */}
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-xs font-medium text-amber-400" style={font}>ALL-IN-ONE PLATFORM</span>
            </div>

            <h1 className="text-4xl xl:text-5xl font-extrabold text-white tracking-tight leading-[1.1] mb-5" style={fontHead}>
              The Intelligent OS for{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-200">
                Modern Businesses
              </span>
            </h1>

            <p className="text-white/40 text-base leading-relaxed mb-10" style={font}>
              Sales, Marketing, HR, Attendance, Payroll — unified in one powerful platform trusted by growing businesses.
            </p>

            {/* Features grid */}
            <div className="grid grid-cols-2 gap-4 mb-10">
              {features.map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                    <f.icon className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white/80" style={fontHead}>{f.label}</p>
                    <p className="text-[11px] text-white/30 mt-0.5" style={font}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-5">
            <div className="flex items-center gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <p className="text-sm text-white/50 italic leading-relaxed mb-4" style={font}>
              "We replaced 4 tools with Dnexus. Team productivity up 60% in the first quarter."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <span className="text-xs font-bold text-white">AK</span>
              </div>
              <div>
                <div className="text-sm font-semibold text-white/80" style={fontHead}>Amit Kumar</div>
                <div className="text-[11px] text-white/30" style={font}>CEO, Digital Agency</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="flex-1 flex items-center justify-center bg-white p-6 lg:p-12 relative">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.15) 1px, transparent 0)",
          backgroundSize: "24px 24px"
        }} />

        <div className="relative z-10 w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="flex items-center justify-center gap-2.5 mb-10 lg:hidden">
            <img src={LOGO_DARK} alt="Dnexus" className="h-10 w-auto" />
          </div>

          {/* Business slug badge */}
          {slugBusiness && (
            <div className="flex items-center gap-3 p-4 mb-6 bg-amber-50 border border-amber-200/60 rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900" style={fontHead}>{slugBusiness.name}</div>
                <div className="text-[11px] text-gray-500" style={font}>
                  {[slugBusiness.city, slugBusiness.industry].filter(Boolean).join(" · ")}
                </div>
              </div>
            </div>
          )}

          <div className="mb-8">
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-2" style={fontHead}>
              {slugBusiness ? `Sign in to ${slugBusiness.name}` : "Welcome back"}
            </h1>
            <p className="text-gray-400 text-sm" style={font}>
              {slugBusiness ? "Enter your credentials to continue" : "Sign in to your Dnexus Business OS account"}
            </p>
          </div>

          <Tabs defaultValue="google" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-gray-100 rounded-2xl p-1 mb-6 h-12">
              <TabsTrigger value="google" data-testid="google-tab"
                className="rounded-xl text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-md text-gray-500 h-10 transition-all" style={font}>
                Google
              </TabsTrigger>
              <TabsTrigger value="password" data-testid="password-tab"
                className="rounded-xl text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-md text-gray-500 h-10 transition-all" style={font}>
                Email & Password
              </TabsTrigger>
            </TabsList>

            <TabsContent value="google" className="mt-0 space-y-4">
              <Button onClick={handleGoogleLogin} data-testid="google-login-button"
                className="w-full h-14 bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 rounded-2xl font-semibold text-sm shadow-sm transition-all hover:scale-[1.01] hover:border-gray-300 active:scale-[0.99] flex items-center justify-center gap-3"
                style={font}>
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Sign in with Google
              </Button>
              <p className="text-center text-xs text-gray-400" style={font}>Secure authentication via Google</p>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 pt-2">
                {["256-bit SSL", "GDPR Compliant", "99.9% Uptime"].map((badge) => (
                  <div key={badge} className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span className="text-[10px] text-gray-400" style={font}>{badge}</span>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="password" className="mt-0">
              <form onSubmit={handlePasswordLogin} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold text-gray-600 mb-2 block" style={font}>Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input id="email" type="email" data-testid="email-input" placeholder="you@company.com"
                      value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="h-14 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-300 rounded-2xl pl-11 text-sm focus:border-amber-400 focus:ring-amber-400/20 focus:bg-white transition-all"
                      style={font} required />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs font-semibold text-gray-600 mb-2 block" style={font}>Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input id="password" type={showPassword ? "text" : "password"} data-testid="password-input"
                      placeholder="Enter your password"
                      value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="h-14 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-300 rounded-2xl pl-11 pr-11 text-sm focus:border-amber-400 focus:ring-amber-400/20 focus:bg-white transition-all"
                      style={font} required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" data-testid="password-login-button" disabled={loading}
                  className="w-full h-14 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-white rounded-2xl font-bold text-sm shadow-lg shadow-amber-500/25 transition-all hover:scale-[1.01] hover:shadow-amber-500/40 active:scale-[0.99] disabled:opacity-50"
                  style={font}>
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Divider */}
          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-300 font-medium" style={font}>OR</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {!slug ? (
            <div className="text-center">
              <p className="text-sm text-gray-400" style={font}>
                Have a business link? Visit{" "}
                <span className="text-amber-600 font-mono text-xs bg-amber-50 px-2 py-1 rounded-lg">/join/your-slug</span> to register
              </p>
            </div>
          ) : (
            <div className="text-center">
              <Link to={`/join/${slug}`} className="text-sm text-amber-600 hover:text-amber-500 font-semibold transition-colors" style={font}>
                Don't have an account? Register here
              </Link>
            </div>
          )}

          <div className="mt-8 flex items-center justify-center gap-4 text-xs text-gray-400" style={font}>
            <Link to="/privacy-policy" className="hover:text-gray-600 transition-colors">Privacy</Link>
            <span className="text-gray-200">·</span>
            <Link to="/terms-of-service" className="hover:text-gray-600 transition-colors">Terms</Link>
            <span className="text-gray-200">·</span>
            <Link to="/" className="hover:text-gray-600 transition-colors flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
